/**
 * Step 2 — Freeform "Describe It" input.
 *
 * Textarea + Generate button → calls `generationApi.synthesizeBrief`.
 * Returns a persisted brief which we surface in the preview pane.  The
 * commit step then upgrades that brief from `draft` if the user accepts.
 */

import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { generationApi, type GenerationBrief } from '@/services/api';

interface Props {
  clientUid: string;
  personaId?: string;
  defaultText?: string;
  attachmentIds?: string[];
  onSynthesized: (brief: GenerationBrief) => void;
  onTextChange?: (text: string) => void;
  buttonLabel?: string;
  showAttachmentNote?: boolean;
}

export function FreeformInput({
  clientUid,
  personaId: _personaId,
  defaultText = '',
  attachmentIds,
  onSynthesized,
  onTextChange,
  buttonLabel = 'Generate Draft →',
  showAttachmentNote = false,
}: Props) {
  const [text, setText] = useState(defaultText);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generate = async () => {
    if (!text.trim() || !clientUid) return;
    setLoading(true);
    setError(null);
    try {
      const brief = await generationApi.synthesizeBrief({
        client_uid: clientUid,
        prompt: text.trim(),
        attachment_ids: attachmentIds,
      });
      onSynthesized(brief);
    } catch (err) {
      setError((err as Error).message || 'Synthesis failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-3">
      <textarea
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          onTextChange?.(e.target.value);
        }}
        placeholder="Describe the creative direction, audience, or campaign concept…"
        rows={8}
        autoFocus
        disabled={loading}
        className="w-full resize-none text-sm text-slate-800 placeholder:text-slate-400 px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-violet-300/40 focus:border-violet-400/60 disabled:bg-slate-50"
      />

      {showAttachmentNote && attachmentIds && attachmentIds.length > 0 && (
        <div className="text-[11px] text-slate-500">
          {attachmentIds.length} material{attachmentIds.length > 1 ? 's' : ''} will be
          included as additional context.
        </div>
      )}

      {error && (
        <div className="text-[12px] text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-2.5">
          {error}
        </div>
      )}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={generate}
          disabled={loading || !text.trim() || !clientUid}
          className="inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white bg-brand-purple rounded-lg hover:bg-brand-purple/90 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? <Loader2 size={12} className="animate-spin" /> : null}
          {buttonLabel}
        </button>
      </div>
    </div>
  );
}
